import { emptySplitApi } from "./emptyApi"

/**
 * Mega Feed structured feed (MD-5679): an ordered list of image / video slots
 * applied in bulk to products and colorways. A run matches an existing asset
 * per (ref × slot) or generates the missing shot; nothing reaches the
 * supplemental feed until the run is applied.
 */

export type MegaFeedSlotKind =
  | "TOP_PERFORMER"
  | "SHOT_TYPE"
  | "STORE_IMAGE"
  | "UGC"
  | "BRAND_TEMPLATE"
  | "AI_GENERATED"
  | "FIXED_ASSET"
  | "LATEST"
  | "CUSTOM"

export type MegaFeedMediaType = "IMAGE" | "VIDEO"

export type MegaFeedRunStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"

export type MegaFeedRunItemStatus =
  | "PENDING"
  | "MATCHED"
  | "GENERATING"
  | "GENERATED"
  | "UNFILLED"
  | "FAILED"

/** One slot kind and the criteria its dropdown offers. */
export interface MegaFeedSlotKindOption {
  kind: MegaFeedSlotKind
  label: string
  description: string
  criteria: { value: string; label: string }[]
  criterionRequired: boolean
}

export interface MegaFeedStructureSlot {
  id: string
  position: number
  mediaType: MegaFeedMediaType
  kind: MegaFeedSlotKind
  criterion: string | null
  label: string | null
  description: string | null
  exampleAssetId: string | null
  exampleAssetUrl: string | null
  fixedAssetId: string | null
  fixedAssetUrl: string | null
  allowGeneration: boolean
}

export interface MegaFeedStructure {
  id: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
  slots: MegaFeedStructureSlot[]
}

/** Body of create / update: the array order is the slot order. */
export interface SaveMegaFeedStructureSlot {
  mediaType: MegaFeedMediaType
  kind: MegaFeedSlotKind
  criterion?: string
  label?: string
  description?: string
  exampleAssetId?: string
  fixedAssetId?: string
  allowGeneration: boolean
}

export interface SaveMegaFeedStructure {
  name: string
  description?: string
  slots: SaveMegaFeedStructureSlot[]
}

export interface MegaFeedStructureRunItem {
  id: string
  slotId: string | null
  slotPosition: number
  /** Product id, or a color ref `<productId>_<color>`. */
  productRefId: string
  status: MegaFeedRunItemStatus
  assetId: string | null
  assetUrl: string | null
  sourceAssetId: string | null
  sourceAssetUrl: string | null
  error: string | null
  /**
   * How the item was filled. `source`: where the existing image (or the
   * generation's reference) came from, `manual` when the user picked it;
   * `detail`: the snapshot set or the ref it is tagged to; `fallback`: the
   * closest existing image, not the requested shot; `selectedBy: "ai"` +
   * `reason`: the vision model chose it among the product's images. `mode`
   * (GENERATED only): recolored from a sibling colorway, or generated from a
   * product photo.
   */
  metadata: {
    source?: "classifier" | "snapshot" | "tagged" | "shopify" | "manual"
    detail?: string
    fallback?: boolean
    selectedBy?: "ai"
    reason?: string
    mode?: "recolor" | "generate"
  } | null
}

export interface MegaFeedStructureRun {
  id: string
  structureId: string
  status: MegaFeedRunStatus
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  error: string | null
  /** When the run was written to the feed; null until applied. */
  appliedAt: string | null
  items: MegaFeedStructureRunItem[]
}

/** A run in a structure's history: item counts instead of items. */
export interface MegaFeedStructureRunSummary {
  id: string
  structureId: string
  status: MegaFeedRunStatus
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  error: string | null
  appliedAt: string | null
  /** How many products / colorways the run covered. */
  refCount: number
  counts: Partial<Record<MegaFeedRunItemStatus, number>>
}

export const megaFeedStructureApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getMegaFeedStructureVocabulary: builder.query<
      MegaFeedSlotKindOption[],
      void
    >({
      query: () => ({ url: "mega-feed/structures/vocabulary", method: "GET" }),
    }),
    listMegaFeedStructures: builder.query<MegaFeedStructure[], void>({
      query: () => ({ url: "mega-feed/structures", method: "GET" }),
      providesTags: ["MegaFeedStructures"],
    }),
    createMegaFeedStructure: builder.mutation<
      MegaFeedStructure,
      SaveMegaFeedStructure
    >({
      query: body => ({ url: "mega-feed/structures", method: "POST", body }),
      invalidatesTags: ["MegaFeedStructures"],
    }),
    updateMegaFeedStructure: builder.mutation<
      MegaFeedStructure,
      SaveMegaFeedStructure & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `mega-feed/structures/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MegaFeedStructures"],
    }),
    archiveMegaFeedStructure: builder.mutation<{ ok: boolean }, string>({
      query: id => ({ url: `mega-feed/structures/${id}`, method: "DELETE" }),
      invalidatesTags: ["MegaFeedStructures"],
    }),
    startMegaFeedStructureRun: builder.mutation<
      MegaFeedStructureRun,
      { structureId: string; productRefIds: string[] }
    >({
      query: ({ structureId, productRefIds }) => ({
        url: `mega-feed/structures/${structureId}/runs`,
        method: "POST",
        body: { productRefIds },
      }),
      invalidatesTags: ["MegaFeedStructures"],
    }),
    /** The structure's own public feed CSV URL (mints the company feed token if missing). */
    getMegaFeedStructureFeedUrl: builder.mutation<{ url: string }, string>({
      query: structureId => ({
        url: `mega-feed/structures/${structureId}/feed-url`,
        method: "POST",
      }),
    }),
    /** A structure's runs, newest first. */
    listMegaFeedStructureRuns: builder.query<
      MegaFeedStructureRunSummary[],
      string
    >({
      query: structureId => ({
        url: `mega-feed/structures/${structureId}/runs`,
        method: "GET",
      }),
      providesTags: ["MegaFeedStructures"],
    }),
    /** Poll while the run is PENDING / RUNNING. */
    getMegaFeedStructureRun: builder.query<MegaFeedStructureRun, string>({
      query: runId => ({
        url: `mega-feed/structures/runs/${runId}`,
        method: "GET",
      }),
      // Refetched after an apply, so a run reopened from history shows it.
      providesTags: ["MegaFeedStructures"],
    }),
    /** The user's pick for one cell of a completed run; null empties it. */
    setMegaFeedStructureRunItem: builder.mutation<
      MegaFeedStructureRun,
      { runId: string; itemId: string; assetId: string | null }
    >({
      query: ({ runId, itemId, assetId }) => ({
        url: `mega-feed/structures/runs/${runId}/items/${itemId}`,
        method: "PUT",
        body: { assetId },
      }),
      // The response is the updated run: put it in the run view's cache.
      async onQueryStarted({ runId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            megaFeedStructureApi.util.updateQueryData(
              "getMegaFeedStructureRun",
              runId,
              () => data,
            ),
          )
        } catch {
          // The caller's unwrap() reports the failure.
        }
      },
    }),
    /** Publishes the run: the structure's feed link serves it from now on. */
    applyMegaFeedStructureRun: builder.mutation<
      { refs: number; assets: number },
      string
    >({
      query: runId => ({
        url: `mega-feed/structures/runs/${runId}/apply`,
        method: "POST",
      }),
      invalidatesTags: ["MegaFeedStructures"],
    }),
  }),
})

export const {
  useGetMegaFeedStructureVocabularyQuery,
  useListMegaFeedStructuresQuery,
  useCreateMegaFeedStructureMutation,
  useUpdateMegaFeedStructureMutation,
  useArchiveMegaFeedStructureMutation,
  useStartMegaFeedStructureRunMutation,
  useListMegaFeedStructureRunsQuery,
  useGetMegaFeedStructureFeedUrlMutation,
  useGetMegaFeedStructureRunQuery,
  useSetMegaFeedStructureRunItemMutation,
  useApplyMegaFeedStructureRunMutation,
} = megaFeedStructureApi
